/**
 * Traditional MIPS submission guidance (business-logic.mdc → Quality Category Score; backlog B19).
 *
 * The top-6 lets the best high-priority measure fill the outcome/high-priority slot. That only
 * holds at CMS if no outcome measure is submitted: CMS reserves the best SUBMITTED outcome first,
 * which would push a better measure out. So when none of the counted measures is an outcome
 * measure, every scored outcome measure must be left out of the submission.
 */
import type { CategoryMeasure, QualityCategoryResult, SelectedCategoryMeasure } from './quality-category';

export interface SubmissionGuidance {
  /** Outcome measures to leave out of the CMS submission, best-scoring first. */
  leaveOut: SelectedCategoryMeasure[];
  /** The high-priority measure that fills the required slot instead. */
  slotFilledBy: SelectedCategoryMeasure;
  /** The measures that count (submit these). */
  counted: SelectedCategoryMeasure[];
}

/** Null when no special handling is needed (an outcome measure already counts, or none has data). */
export function submissionGuidance(measures: CategoryMeasure[], quality: QualityCategoryResult): SubmissionGuidance | null {
  if (quality.selected.length === 0 || quality.selected.some(m => m.isOutcome)) return null;
  const slotFilledBy = quality.selected.find(m => m.isHighPriority);
  if (!slotFilledBy) return null;

  const counted = new Set(quality.selected.map(m => `${m.measureId}:${m.submissionMethod}`));
  const leaveOut = measures
    .filter(m => m.isOutcome && Number.isFinite(Number(m.points)) && m.points !== null)
    .filter(m => !counted.has(`${m.measureId}:${m.submissionMethod}`))
    .map(m => ({ ...m, points: Number(m.points) }))
    .sort((a, b) => b.points - a.points);
  if (leaveOut.length === 0) return null;

  return { leaveOut, slotFilledBy, counted: quality.selected };
}
